/**
 * Date utilities for HR Command Center
 * Handles timezone-aware date calculations with Monday-Sunday weeks
 */

// Helper to get current date in user's timezone (stripping time)
export const getToday = (): Date => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export const formatDate = (
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  }).format(d)
}

export const formatTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(d)
}

export const isToday = (dateStr: string): boolean => {
  const today = getToday()
  const d = new Date(dateStr)
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  )
}

export const isBirthdayToday = (birthdayMMDD: string): boolean => {
  const today = getToday()
  const [month, day] = birthdayMMDD.split('-').map(Number)
  // Note: Month is 0-indexed in JS Date, but 1-indexed in our MM-DD string
  return today.getMonth() === month - 1 && today.getDate() === day
}

// Check if a date falls within the current week (Monday to Sunday)
export const isThisWeek = (dateStr: string): boolean => {
  const d = new Date(dateStr)
  const today = getToday()

  // Get current day of week (0 = Sunday, 1 = Monday, ... 6 = Saturday)
  const currentDay = today.getDay()

  // Calculate Monday of this week
  // If Sunday (0), subtract 6 days. If Monday (1), subtract 0 days.
  const diffToMonday = currentDay === 0 ? 6 : currentDay - 1

  const monday = new Date(today)
  monday.setDate(today.getDate() - diffToMonday)
  monday.setHours(0, 0, 0, 0)

  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)

  return d >= monday && d <= sunday
}

export const isThisMonth = (dateStr: string): boolean => {
  const d = new Date(dateStr)
  const today = getToday()
  return (
    d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear()
  )
}

export const isBirthdayThisWeek = (birthdayMMDD: string): boolean => {
  const today = getToday()
  const currentYear = today.getFullYear()
  const [month, day] = birthdayMMDD.split('-').map(Number)

  // Create a date object for the birthday this year
  const birthdayThisYear = new Date(currentYear, month - 1, day)

  // Check if it's this week
  if (isThisWeek(birthdayThisYear.toISOString())) return true

  // Edge case: if week crosses year boundary (e.g. Dec 30 - Jan 5)
  // We might need to check next year's birthday if we are in Dec, or prev year if in Jan
  // For simplicity in this mock, we'll stick to current year check primarily
  return false
}

export const getMonthDays = (year: number, month: number): (Date | null)[] => {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)

  const days: (Date | null)[] = []

  // Add empty slots for days before the first of the month
  // We want Monday to be the first column
  let startDay = firstDay.getDay() // 0 = Sun, 1 = Mon
  if (startDay === 0) startDay = 7 // Make Sunday 7

  // Loop from 1 to startDay - 1 (because Monday is 1)
  for (let i = 1; i < startDay; i++) {
    days.push(null)
  }

  // Add all days of the month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i))
  }

  return days
}
